<template>
  <div>
    <panel-block v-if="selectedObjectModelAction.can(ObjectModelActionCapability.labelable)" id="pn-name">
      <panel-item>
        <label :for="selectedObjectModelAction.idForProp('label')">Action Name</label>
        <expandable-textarea
          :id="selectedObjectModelAction.idForProp('label')"
          v-model="selectedObjectModelAction.label_title"
          placeholder="Enter action name..."></expandable-textarea>
      </panel-item>
    </panel-block>

    <panel-block v-if="selectedObjectModelAction.can(ObjectModelActionCapability.formLinkable)" id="pn-linked-form">
      <panel-item>
        <label :for="selectedObjectModelAction.idForProp('linked_form_id')" class="a-select-label">Form to Fill</label>
        <a-select
          v-model="linkedForm"
          bind-by-object
          :options="linkedFormVSelectOptions"
          compress
          :id="selectedObjectModelAction.idForProp('linked_form_id')"
          placeholder="Search forms..."></a-select>
      </panel-item>
    </panel-block>

    <panel-block v-if="selectedObjectModelAction.can(ObjectModelActionCapability.runProcessable)" id="pn-process-flow">
      <panel-item>
        <label :for="selectedObjectModelAction.idForProp('linked_process_flow_id')">Process</label>
        <a-select
          placeholder="Select Process..."
          v-model="linkedProcessFlow"
          compress
          bind-by-object
          :id="selectedObjectModelAction.idForProp('linked_process_flow_id')"
          :options="linkedProcessFlowVSelectOptions"></a-select>
        <button v-if="selectedObjectModelAction.linked_process_flow_id"
                id="configure-process-flow"
                class="btn grey-to-blue full-sz"
                @click="showProcessModal">
          Configure
        </button>
        <view-configure-process-flow-modal
          @toggle-modal="hideModal(vueModalID.configureObjectModelProcessFlow)"
          v-if="modalStateFor(vueModalID.configureObjectModelProcessFlow).isVisible"
          :state="modalStateFor(vueModalID.configureObjectModelProcessFlow)"
          :index="vueModalID.configureObjectModelProcessFlow"
          :parentModel="selectedObjectModelAction"
          :object-model="selectedObjectModel"
          :process-flow="linkedProcessFlow">
        </view-configure-process-flow-modal>
      </panel-item>
    </panel-block>

    <panel-block v-if="selectedObjectModelAction.can(ObjectModelActionCapability.documentLinkable)" id="pn-linked-document">
      <panel-item>
        <label :for="selectedObjectModelAction.idForProp('linked_document_id')" class="a-select-label">Choose a document</label>
        <a-select
          v-model="linkedDocument"
          bind-by-object
          :options="linkedDocumentVSelectOptions"
          compress
          :id="selectedObjectModelAction.idForProp('linked_document_id')"
        ></a-select>
      </panel-item>
    </panel-block>

    <panel-block v-if="selectedObjectModelAction.can(ObjectModelActionCapability.accountLinkable)" id="pn-linked-account">
      <panel-item>
        <label :for="selectedObjectModelAction.idForProp('linked_account_relationship_id')" class="a-select-label">
          Assign to which account relationship?
        </label>
        <a-select
          v-model="linkedAccountRelationship"
          bind-by-object
          :options="linkedAccountRelationshipVSelectOptions"
          compress
          placeholder="Select account relationship..."
          :id="selectedObjectModelAction.idForProp('linked_account_relationship_id')"
        ></a-select>
      </panel-item>
    </panel-block>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import {ObjectModelActionCapability} from "../../../common/models/objectModelActions/objectModelActionCapability";
  import selectedMixin from "../mixins/selectedMixin";
  import {IVOptionSetEndpoint, IVOptionSetLocal} from "../../../common/interfaces/vSelectInterfaces";
  import ActionPermitteesConfig from "./action-permittees-config.vue";
  import permitteeVOptionSetMixin from "~/mixins/permitteeVOptionSetMixin";
  import {ObjectModelActionType} from "../../../common/enums/objectModelActionType";
  import {ObjectModelDefaultActionType} from "~/enums/objectModelDefaultActionType";
  import {IShallowProcessFlow} from "~/interfaces/processFlowInterfaces";
  import {Route} from "~/enums/route";
  import {RouteHelper} from "~/helpers/routeHelper";
  import {ObjectModelRelationType} from "~/enums/objectModelRelationType";
  import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
  import ViewConfigureProcessFlowModal from "./view-configure-process-flow-modal.vue";
  import ProcessFlowSubmissionMappedVariable from "~/models/processFlows/processFlowSubmissionMappedVariable";

  export default Vue.extend({
    mixins: [
        selectedMixin,
        permitteeVOptionSetMixin,
    ],
    components: {
      ActionPermitteesConfig,
      ViewConfigureProcessFlowModal,
    },
    data: function () {
      return {
        ObjectModelActionCapability,
      };
    },
    computed: {
      linkedForm: {
        get(): any {
          return {
            id: this.selectedObjectModelAction.linked_form_id,
            title: this.selectedObjectModelAction.linked_form_title,
          };
        },
        set(form: any) {
          this.selectedObjectModelAction.linked_form_id = form.id;
          this.selectedObjectModelAction.linked_form_title = form.title;
        }
      },
      linkedDocument: {
        get(): any {
          return {
            id: this.selectedObjectModelAction.linked_document_id,
            title: this.selectedObjectModelAction.linked_document_title,
          };
        },
        set(document: any) {
          this.selectedObjectModelAction.linked_document_id = document.id;
          this.selectedObjectModelAction.linked_document_title = document.title;
        }
      },
      linkedProcessFlow: {
        get(): IShallowProcessFlow {
          if (this.selectedObjectModelAction.linked_process_flow_id) {
            return this.selectedObjectModelAction.process_flow;
          }
        },
        set(data: IShallowProcessFlow): void {
          ProcessFlowSubmissionMappedVariable.destroyAll(this.selectedObjectModelAction)
          this.selectedObjectModelAction.setLinkedProcessFlow({
            id: data.id,
            title: data.title,
            is_published: data.is_published,
            user_interactions_enabled: data.user_interactions_enabled,
            variables: data.variables,
          })
        },
      },
      linkedAccountRelationship: {
        get(): any {
          return {
            id: this.selectedObjectModelAction.linked_account_relationship_id,
            label_title: this.selectedObjectModelAction.linked_account_relationship_title,
          };
        },
        set(accountRelationship: any) {
          this.selectedObjectModelAction.linked_account_relationship_id = accountRelationship.id;
          this.selectedObjectModelAction.linked_account_relationship_title = accountRelationship.label_title;
        }
      },
      linkedFormVSelectOptions: {
        get(): IVOptionSetEndpoint {
          return {
            valueKey: 'id',
            labelKey: 'title',
            rootKey: 'forms',
            endpoint: '/organizations/' + this.selectedObjectModel.organization_id + '/search_forms',
            endpointOptions: {
              per_page: 5,
              where: {
                application_id: this.selectedObjectModel.application_id
              }
            },
            selectedOption: this.linkedForm,
          };
        }
      },
      linkedDocumentVSelectOptions: {
        get(): IVOptionSetEndpoint {
          return {
            valueKey: 'id',
            labelKey: 'title',
            rootKey: 'certificates',
            endpoint: '/organizations/' + this.selectedObjectModel.organization_id + '/search_certificates',
            endpointOptions: {
              per_page: 5,
              where: {
                linked_model_ids: [this.selectedObjectModel.client_id],
                application_id: this.selectedObjectModel.application_id
              }
            },
            selectedOption: this.linkedDocument,
          };
        }
      },
      linkedProcessFlowVSelectOptions: {
        get(): IVOptionSetEndpoint {
          return {
            valueKey: 'id',
            labelKey: 'title',
            endpoint: RouteHelper.path({ route: Route.objectModelSearchProcessFlows, modelOrId: this.selectedObjectModel.id }),
            endpointOptions: {
              where: {
                application_id: this.selectedObjectModel.application_id
              }
            },
            endpointFilters: {organization_id: this.selectedObjectModel.organization.id, per_page: 5},
            rootKey: 'process_flows',
            selectedOption: this.linkedProcessFlow
          };
        }
      },
      selectedAccountRelationships(): ObjectModelRelation[] {
        return ObjectModelRelation.repo.findWhere(omr => omr.relation_type === ObjectModelRelationType.Account);
      },
      linkedAccountRelationshipVSelectOptions(): IVOptionSetLocal {
        const accountRelationships = this.selectedObjectModel.objectModelRelationsOfType(ObjectModelRelationType.Account);
        const options = accountRelationships.map(accountRelation => {
          const title = accountRelation.label_title || "Untitled Relationship";
          return {
            id: accountRelation.id,
            label_title: title
          }
        })
        return {
          options,
          valueKey: 'id',
          labelKey: 'label_title',
          selectedOption: this.linkedAccountRelationship,
        }
      },
      performActionRestrictableVOptionSets() {
        if (this.selectedObjectModelAction.action_type === ObjectModelActionType.DefaultAction && this.selectedObjectModelAction.default_action_type !== ObjectModelDefaultActionType.Delete) {
          return this.usersAndGroupsVOptionSets;
        } else if (this.selectedObjectModelAction.action_type === ObjectModelActionType.CustomNotification) {
          return [
            this.recordHolderVOptionSet,
            this.orgGroupsVOptionSet,
            this.usersVOptionSet,
          ];
        } else {
          return this.objectModelActionPerformerVOptionSets;
        }
      }
    },
    methods: {
      showProcessModal() {
        this.showModal(this.vueModalID.configureObjectModelProcessFlow)
      }
    },
  });
</script>